import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierProductsPage from './pages/SupplierProductsPage';
import ProductApprovalPage from './pages/ProductApprovalPage';
import OrdersPage from './pages/OrdersPage';
import AnalyticsDashboardPage from './pages/analytics/AnalyticsDashboardPage';
import RevenueAnalyticsPage from './pages/analytics/RevenueAnalyticsPage';
import OrdersAnalyticsPage from './pages/analytics/OrdersAnalyticsPage';
import SuppliersAnalyticsPage from './pages/analytics/SuppliersAnalyticsPage';
import UsersAnalyticsPage from './pages/analytics/UsersAnalyticsPage';
import TransportersAnalyticsPage from './pages/analytics/TransportersAnalyticsPage';
import ProductsAnalyticsPage from './pages/analytics/ProductsAnalyticsPage';
import CategoriesPage from './pages/CategoriesPage';
import PricingPage from './pages/PricingPage';
import InventoryPage from './pages/InventoryPage';
import TransportersPage from './pages/TransportersPage';
import TransporterProfilePage from './pages/transporter/TransporterProfilePage';
import MobileUsersPage from './pages/MobileUsersPage';
import MarketingPage from './pages/MarketingPage';
import EarningsDashboardPage from './pages/earnings/EarningsDashboardPage';
import PaymentsOverviewPage from './pages/earnings/PaymentsOverviewPage';
import PendingPayoutsPage from './pages/earnings/PendingPayoutsPage';
import EarningsHistoryPage from './pages/earnings/EarningsHistoryPage';
import EarningsStatisticsPage from './pages/earnings/EarningsStatisticsPage';
import BatchStatusPage from './pages/earnings/BatchStatusPage';
import WithdrawalsPage from './pages/earnings/WithdrawalsPage';
import BankVerificationPage from './pages/earnings/BankVerificationPage';
import InvoicesPage from './pages/InvoicesPage';
import ContentManagementPage from './pages/ContentManagementPage';
import SettingsPage from './pages/SettingsPage';
// Banners, Offers, and Featured Deals are now consolidated in MarketingPage
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { AlertProvider } from './contexts/AlertContext';
import { AlertOverlay } from './components/alerts/AlertOverlay';
import { ConfirmProvider } from './components/ui/confirm-dialog';

import { Toaster } from 'sonner';

function App() {
  // Check for authentication by looking for the token in localStorage
  const isAuthenticated = !!localStorage.getItem('auth_token');

  return (
    <ThemeProvider>
      <NotificationProvider>
        <AlertProvider>
          <WebSocketProvider>
            <ConfirmProvider>
            <AlertOverlay />
            <Toaster richColors closeButton position="top-right" />
            <Router>
              <Routes>
                <Route path="/login" element={
                  isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
                } />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <DashboardPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* User Management Routes */}
                <Route
                  path="/suppliers"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <SuppliersPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/mobile-users"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <MobileUsersPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Content management: FAQ, support, legal, status messages */}
                <Route
                  path="/content/faq"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <ContentManagementPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/content"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <ContentManagementPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Transporter Management Routes */}
                <Route
                  path="/transporters"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <TransportersPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/transporters/:id"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <TransporterProfilePage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Product Management Routes */}
                <Route
                  path="/categories"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <CategoriesPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/supplier-products"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <SupplierProductsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/products/approvals"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <ProductApprovalPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/pricing"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <PricingPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/inventory"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <InventoryPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Order Management Routes */}
                <Route
                  path="/orders"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <OrdersPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/orders/pending"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <OrdersPage initialStatus="pending" />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/orders/processing"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <OrdersPage initialStatus="processing" />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/orders/completed"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <OrdersPage initialStatus="completed" />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/orders/cancelled"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <OrdersPage initialStatus="cancelled" />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Analytics Routes */}
                <Route
                  path="/analytics/dashboard"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <AnalyticsDashboardPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/analytics/revenue"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <RevenueAnalyticsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/analytics/orders"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <OrdersAnalyticsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/analytics/suppliers"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <SuppliersAnalyticsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/analytics/users"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <UsersAnalyticsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/analytics/transporters"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <TransportersAnalyticsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/analytics/products"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <ProductsAnalyticsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Legacy analytics route - redirect to dashboard */}
                <Route
                  path="/analytics/sales"
                  element={
                    isAuthenticated ? (
                      <Navigate to="/analytics/dashboard" replace />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Marketing & Promotions Routes */}
                <Route
                  path="/marketing"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <MarketingPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Redirect old promotion routes to consolidated Marketing page */}
                <Route
                  path="/banners"
                  element={<Navigate to="/marketing" replace />}
                />
                <Route
                  path="/offers"
                  element={<Navigate to="/marketing" replace />}
                />
                <Route
                  path="/featured-deals"
                  element={<Navigate to="/marketing" replace />}
                />

                {/* Settings */}
                <Route
                  path="/settings"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <SettingsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Earnings & Payments Routes */}
                <Route
                  path="/earnings"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <PaymentsOverviewPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/overview"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <PaymentsOverviewPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/dashboard"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <EarningsDashboardPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/pending-payouts"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <PendingPayoutsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/history"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <EarningsHistoryPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/statistics"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <EarningsStatisticsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/batch-status"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <BatchStatusPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/withdrawals"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <WithdrawalsPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/earnings/bank-verification"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <BankVerificationPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/invoices"
                  element={
                    isAuthenticated ? (
                      <Layout>
                        <InvoicesPage />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Promotion Management Routes - Redirected to consolidated Marketing page */}

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
            </ConfirmProvider>
          </WebSocketProvider>
        </AlertProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
